import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow, parseISO } from 'date-fns';
import {
  UserPlusIcon,
  BuildingOfficeIcon,
  CpuChipIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

interface Activity {
  id: string;
  type: 'user_created' | 'venue_created' | 'device_online' | 'device_offline' | 'violation' | 'violation_resolved';
  title: string;
  description: string;
  timestamp: string;
  metadata?: {
    user_name?: string;
    venue_name?: string;
    device_name?: string;
    severity?: 'low' | 'medium' | 'high';
  };
}

interface ActivityStreamProps {
  activities: Activity[];
  maxItems?: number;
}

const ActivityStream: React.FC<ActivityStreamProps> = ({
  activities,
  maxItems = 10,
}) => {
  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'user_created':
        return <UserPlusIcon className="h-5 w-5" />;
      case 'venue_created':
        return <BuildingOfficeIcon className="h-5 w-5" />;
      case 'device_online':
        return <CpuChipIcon className="h-5 w-5" />;
      case 'device_offline':
        return <CpuChipIcon className="h-5 w-5" />;
      case 'violation':
        return <ExclamationTriangleIcon className="h-5 w-5" />;
      case 'violation_resolved':
        return <CheckCircleIcon className="h-5 w-5" />;
      default:
        return <ArrowRightIcon className="h-5 w-5" />;
    }
  };

  const getActivityColor = (type: Activity['type']) => {
    switch (type) {
      case 'user_created':
        return { bg: 'bg-blue-500', text: 'text-blue-500', glow: 'rgba(59, 130, 246, 0.3)' };
      case 'venue_created':
        return { bg: 'bg-green-500', text: 'text-green-500', glow: 'rgba(16, 185, 129, 0.3)' };
      case 'device_online':
        return { bg: 'bg-green-500', text: 'text-green-500', glow: 'rgba(16, 185, 129, 0.3)' };
      case 'device_offline':
        return { bg: 'bg-gray-500', text: 'text-gray-500', glow: 'rgba(107, 114, 128, 0.3)' };
      case 'violation':
        return { bg: 'bg-red-500', text: 'text-red-500', glow: 'rgba(239, 68, 68, 0.3)' };
      case 'violation_resolved':
        return { bg: 'bg-green-500', text: 'text-green-500', glow: 'rgba(16, 185, 129, 0.3)' };
      default:
        return { bg: 'bg-gray-500', text: 'text-gray-500', glow: 'rgba(107, 114, 128, 0.3)' };
    }
  };

  const displayActivities = activities.slice(0, maxItems);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Activity Stream
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Real-time system events
          </p>
        </div>
        <motion.div
          className="flex items-center gap-2"
          animate={{
            opacity: [1, 0.5, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
          }}
        >
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-xs text-gray-600 dark:text-gray-400">LIVE</span>
        </motion.div>
      </div>

      {/* Activity list */}
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-[21px] top-0 bottom-0 w-px bg-gradient-to-b from-gray-300 via-gray-200 to-transparent dark:from-gray-700 dark:via-gray-800" />

        <div className="space-y-6">
          <AnimatePresence mode="popLayout">
            {displayActivities.map((activity, index) => {
              const colors = getActivityColor(activity.type);

              return (
                <motion.div
                  key={activity.id}
                  className="relative flex gap-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.05,
                  }}
                  layout
                >
                  {/* Icon circle */}
                  <div className="relative flex-shrink-0">
                    <motion.div
                      className={`w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center text-white shadow-lg relative z-10`}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 200,
                        damping: 15,
                        delay: index * 0.05,
                      }}
                    >
                      {getActivityIcon(activity.type)}
                    </motion.div>

                    {/* Pulse effect for recent activities */}
                    {index < 3 && (
                      <motion.div
                        className="absolute inset-0 rounded-full"
                        style={{ backgroundColor: colors.glow }}
                        animate={{
                          scale: [1, 1.5, 1],
                          opacity: [0.5, 0, 0.5],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: index * 0.3,
                        }}
                      />
                    )}
                  </div>

                  {/* Content */}
                  <motion.div
                    className="flex-1 bg-gray-50 dark:bg-gray-900 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                          {activity.title}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {activity.description}
                        </p>

                        {/* Metadata tags */}
                        {activity.metadata && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {activity.metadata.user_name && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                                <UserPlusIcon className="h-3 w-3 mr-1" />
                                {activity.metadata.user_name}
                              </span>
                            )}
                            {activity.metadata.venue_name && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                                <BuildingOfficeIcon className="h-3 w-3 mr-1" />
                                {activity.metadata.venue_name}
                              </span>
                            )}
                            {activity.metadata.device_name && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                                <CpuChipIcon className="h-3 w-3 mr-1" />
                                {activity.metadata.device_name}
                              </span>
                            )}
                            {activity.metadata.severity && (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                                activity.metadata.severity === 'high' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' :
                                activity.metadata.severity === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
                                'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                              }`}>
                                {activity.metadata.severity.toUpperCase()}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Timestamp */}
                      <div className="flex-shrink-0">
                        <span className="text-xs text-gray-500 dark:text-gray-500 font-mono">
                          {formatDistanceToNow(parseISO(activity.timestamp), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Empty state */}
        {displayActivities.length === 0 && (
          <motion.div
            className="text-center py-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="text-gray-400 dark:text-gray-600 mb-2">
              <ArrowRightIcon className="h-12 w-12 mx-auto opacity-50" />
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              No recent activity
            </p>
          </motion.div>
        )}

        {/* Load more indicator */}
        {activities.length > maxItems && (
          <motion.div
            className="mt-6 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span>+{activities.length - maxItems} more activities</span>
              <ArrowRightIcon className="h-4 w-4" />
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ActivityStream;
